import { get } from 'lodash'
import moment from 'moment'

import httpClient, { catchError } from '@emmacare/common/api/httpClient'

import { patientUuidSelector } from 'src/store/modules/patient'
import {
  setPathwayActiveTab,
  setPathwayAssignedList,
  setPathwayList,
  setPathwayPatientList,
  setPathwayPatientValues,
  setPathwayQuestionListModal,
  setPatientAnswerList
} from 'src/store/modules/patient/pathways'

import {
  CLINICAL_PATHWAYS,
  CLINICAL_PATHWAYS_ASSIGNED,
  CLINICAL_PATHWAYS_REPORT_EXPORT,
  CLINICAL_PATHWAY_PATIENT,
  CLINICAL_PATHWAY_PATIENT_ALERT_STATUS_DECISION,
  CLINICAL_PATHWAY_PATIENT_ANSWER_DELETE,
  CLINICAL_PATHWAY_PATIENT_ANSWER_LIST,
  CLINICAL_PATHWAY_PATIENT_CHECK_IN,
  CLINICAL_PATHWAY_PATIENT_COPY,
  CLINICAL_PATHWAY_PATIENT_CREATE_CHECK_IN,
  CLINICAL_PATHWAY_PATIENT_LIST,
  DATE_TIME_FORMAT_FOR_SERVER,
  DETAILED_QUERY_PARAM,
  GET_CHECK_IN_LIST_QUESTIONS,
  GET_CLINICAL_PATHWAY_QUESTIONS,
  PATCH_PATIENT_CHECK_IN,
  PATHWAY_RESPONSE_SUCCESS_SAVE_MESSAGE,
  PATHWAY_SUCCESS_SAVE_MESSAGE,
  PATHWAY_TAB_LIST,
  POST_PATIENT_CHECK_IN
} from 'src/constants'
import { showSuccessModal } from 'src/services/dialogs'
import { exportReport } from 'src/utils/Functions'

export const getPathwayList = async (config = {}) => {
  try {
    const { params = {}, query = {} } = config

    const response = await httpClient.get({
      pathname: CLINICAL_PATHWAYS,
      params,
      query
    })

    return response?.items || []
  } catch (err) {
    catchError(err)

    return []
  }
}

export const fetchPathwayList =
  (config = {}) =>
  async (dispatch) => {
    try {
      const pathwayList = await getPathwayList(config)

      return dispatch(setPathwayList(pathwayList))
    } catch (err) {
      return catchError(err)
    }
  }

export const checkPathwayListActive =
  (params = {}, query = {}) =>
  async () => {
    try {
      await httpClient.get({
        pathname: CLINICAL_PATHWAYS,
        params,
        query
      })

      return true
    } catch (err) {
      catchError(err)
      return false
    }
  }

export const fetchPathwayListAssigned =
  (config = {}) =>
  async (dispatch) => {
    try {
      const { params = {}, query = {} } = config

      const items = await httpClient.get({
        pathname: CLINICAL_PATHWAYS_ASSIGNED,
        params,
        query
      })

      dispatch(setPathwayAssignedList(items))
    } catch (err) {
      catchError(err)
    }
  }

export const saveClinicalPathwayPatient =
  (config, history) => async (dispatch, getState) => {
    try {
      const { params = {}, payload = {} } = config
      const patientUuid = patientUuidSelector(getState())

      await httpClient.post(
        {
          pathname: CLINICAL_PATHWAY_PATIENT_LIST,
          params
        },
        payload
      )

      return showSuccessModal({
        content: PATHWAY_SUCCESS_SAVE_MESSAGE,
        centered: true,
        keyboard: false,
        onOk: () => {
          dispatch(setPathwayActiveTab(PATHWAY_TAB_LIST[1]))

          return history.push(`/patients/${patientUuid}/clinical-pathways`)
        }
      })
    } catch (err) {
      return catchError(err, { ignoredValuesList: ['frequency.reminders'] })
    }
  }

export const updateClinicalPathwayPatient =
  (config, history) => async (dispatch, getState) => {
    try {
      const { params = {}, payload = {} } = config
      const patientUuid = patientUuidSelector(getState())

      await httpClient.patch(
        {
          pathname: CLINICAL_PATHWAY_PATIENT,
          params
        },
        payload
      )

      return showSuccessModal({
        content: PATHWAY_SUCCESS_SAVE_MESSAGE,
        centered: true,
        keyboard: false,
        onOk: () => {
          if (history) {
            dispatch(setPathwayActiveTab(PATHWAY_TAB_LIST[1]))

            return history.push(`/patients/${patientUuid}/clinical-pathways`)
          }

          return false
        }
      })
    } catch (err) {
      return catchError(err, { ignoredValuesList: ['frequency.reminders'] })
    }
  }

export const updateClinicalPathwayFinishMessages =
  (config, setFinishMessagesTranslations) => async (dispatch) => {
    try {
      const { params = {}, payload = {} } = config
      const {
        params: { uuid }
      } = config

      await httpClient.patch(
        {
          pathname: CLINICAL_PATHWAY_PATIENT,
          params
        },
        payload
      )

      dispatch(
        fetchPathwayPatientValues(
          { uuid },
          DETAILED_QUERY_PARAM,
          setFinishMessagesTranslations
        )
      )
    } catch (err) {
      return catchError(err)
    }
  }

export const fetchPathwayPatientList =
  (config = {}) =>
  async (dispatch) => {
    try {
      const { params = {}, query = {} } = config
      const response = await httpClient.get({
        pathname: CLINICAL_PATHWAY_PATIENT_LIST,
        params,
        query
      })
      const pathwayPatientList = get(response, 'items', [])

      return dispatch(setPathwayPatientList(pathwayPatientList))
    } catch (err) {
      return catchError(err)
    }
  }

export const fetchPathwayPatientValues =
  (params = {}, query = {}, setFinishMessagesTranslations) =>
  async (dispatch) => {
    try {
      const response = await httpClient.get({
        pathname: CLINICAL_PATHWAY_PATIENT,
        params,
        query
      })

      if (setFinishMessagesTranslations) {
        setFinishMessagesTranslations(response)
      }
      return dispatch(setPathwayPatientValues(response))
    } catch (err) {
      return catchError(err)
    }
  }

export const deletePathwayPatientPathway =
  (params = {}) =>
  async () => {
    try {
      return httpClient.delete({
        pathname: CLINICAL_PATHWAY_PATIENT,
        params
      })
    } catch (err) {
      return catchError(err)
    }
  }

export const getPathwayQuestionList =
  (config = {}) =>
  async () => {
    try {
      const { params = {}, query = {} } = config
      const questions = []
      const response = await httpClient.get({
        pathname: CLINICAL_PATHWAYS,
        params,
        query
      })

      get(response, 'items', []).forEach((item) =>
        questions.push(...item.questions)
      )

      return questions
    } catch (err) {
      catchError(err)
    }
  }

export const fetchPathwayQuestionList =
  (config = {}) =>
  async (dispatch) => {
    try {
      const { params = {}, query = {} } = config
      const response = await httpClient.get({
        pathname: CLINICAL_PATHWAYS,
        params,
        query
      })
      const item = get(response, 'items.0', {})

      dispatch(setPathwayPatientValues(item))

      return item
    } catch (err) {
      return catchError(err)
    }
  }

export const getChekInQuestionList = (checkInObj, questionObj) => async () => {
  try {
    const { params = {}, query = {} } = checkInObj
    const checkIn = await httpClient.get({
      pathname: CLINICAL_PATHWAY_PATIENT_CHECK_IN,
      params,
      query
    })
    const pathway = await httpClient.get({
      pathname: CLINICAL_PATHWAYS,
      params,
      query
    })

    const checkInQuestions = get(checkIn, 'questions', [])
    const questions = get(pathway, 'items.0.questions', []).map((question) => {
      let disabled = false
      checkInQuestions.forEach((item) => {
        if (question.uuid === item.uuid) {
          disabled = item.answer !== null
        }
      })

      return {
        ...question,
        disabled
      }
    })

    return questions
  } catch (err) {
    return catchError(err)
  }
}

export const fetchPatientAnswerList =
  (config = {}) =>
  async (dispatch) => {
    const { params = {}, query = {} } = config

    try {
      const { items = [], meta = {} } = await httpClient.get({
        pathname: CLINICAL_PATHWAY_PATIENT_ANSWER_LIST,
        params,
        query
      })

      dispatch(setPatientAnswerList(items))

      return meta
    } catch (err) {
      return catchError(err)
    }
  }

export const getPatientAnswerValues =
  (config = {}) =>
  async () => {
    const { params = {}, query = {} } = config

    try {
      const patientAnswerList = await httpClient.get({
        pathname: CLINICAL_PATHWAY_PATIENT_ANSWER_LIST,
        params,
        query
      })

      return get(patientAnswerList, ['items', 0], {})
    } catch (err) {
      return catchError(err)
    }
  }

export const savePathwayPatientAnswer =
  (config = {}) =>
  async () => {
    const { params = {}, payload = {} } = config

    try {
      await httpClient.post(
        {
          pathname: POST_PATIENT_CHECK_IN,
          params
        },
        payload
      )

      showSuccessModal({
        content: PATHWAY_RESPONSE_SUCCESS_SAVE_MESSAGE,
        centered: true,
        keyboard: false
      })

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const updatePathwayPatientAnswer =
  (config = {}) =>
  async () => {
    const { params = {}, payload = {} } = config

    try {
      await httpClient.patch(
        {
          pathname: PATCH_PATIENT_CHECK_IN,
          params
        },
        payload
      )

      showSuccessModal({
        content: PATHWAY_RESPONSE_SUCCESS_SAVE_MESSAGE,
        centered: true,
        keyboard: false
      })

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const deletePathwayPatientAnswer =
  (config = {}) =>
  async () => {
    try {
      const { params = {} } = config

      return httpClient.delete({
        pathname: CLINICAL_PATHWAY_PATIENT_ANSWER_DELETE,
        params
      })
    } catch (err) {
      return catchError(err)
    }
  }

export const createPathwayPatientCheckIn =
  (config = {}) =>
  async () => {
    try {
      const { params = {}, payload = {} } = config

      await httpClient.post(
        {
          pathname: CLINICAL_PATHWAY_PATIENT_CREATE_CHECK_IN,
          params
        },
        payload
      )

      return showSuccessModal({
        content: 'Check-in successfully created!',
        centered: true,
        keyboard: false
      })
    } catch (err) {
      return catchError(err)
    }
  }

export const exportPatientClinicalPathwayReport = (config) => async () => {
  const updConfig = {
    pathname: CLINICAL_PATHWAYS_REPORT_EXPORT,
    ...config
  }

  await exportReport(
    updConfig,
    `patient_clinical_pathway_${moment().format(
      DATE_TIME_FORMAT_FOR_SERVER
    )}.xls`
  )
}

export const fetchClinicalPathwayQuestions =
  (config = {}) =>
  async (dispatch) => {
    const { params = {}, query = {} } = config

    try {
      const items = await httpClient.get({
        pathname: GET_CLINICAL_PATHWAY_QUESTIONS,
        params,
        query
      })

      dispatch(setPathwayQuestionListModal(items))
    } catch (err) {
      catchError(err)
    }
  }

export const fetchCheckInQuestions =
  (config = {}) =>
  async (dispatch) => {
    const { params = {}, query = {} } = config

    try {
      const items = await httpClient.get({
        pathname: GET_CHECK_IN_LIST_QUESTIONS,
        params,
        query
      })

      dispatch(setPathwayQuestionListModal(items))
    } catch (err) {
      catchError(err)
    }
  }

export const copyPatientClinicalPathway =
  (config = {}) =>
  async () => {
    const { params = {} } = config

    try {
      await httpClient.post({
        pathname: CLINICAL_PATHWAY_PATIENT_COPY,
        params
      })

      return true
    } catch (err) {
      catchError(err)

      return false
    }
  }

export const getClinicalPathwayAlertStatusDecision =
  (config = {}) =>
  async () => {
    const { params = {}, payload = {} } = config

    try {
      const { askConfirmQuestion = false } = await httpClient.request({
        method: 'post',
        url: {
          pathname: CLINICAL_PATHWAY_PATIENT_ALERT_STATUS_DECISION,
          params
        },
        payload
      })

      return { askConfirmQuestion, hasError: false }
    } catch (err) {
      catchError(err)

      return { askConfirmQuestion: false, hasError: true }
    }
  }
